import React from "react";
import styled from "styled-components";
import Hero from "components/pages/index/Hero";
import About from "components/pages/index/About";
import Stories, { getLatestStoriesQuery } from "components/pages/index/Stories";
import { FooterWithState } from "components/Footer";
import withApollo from "components/higher-order/withApollo";
import PageContainer from "components/containers/PageContainer";
import { JustSignedUpListener } from "components/listeners/JustSignedUpListener";
import { JustGaveGiftListener } from "components/listeners/JustGaveGiftListener";
import { NewsletterPopupForm } from "components/pages/index/NewsletterPopup";
import { isNil } from "lodash";
import { parseCookies } from "nookies";
import uuidv4 from "uuid/v4";
import { useHomepageState } from "components/optimized/Homepage/Homepage.hooks";
import request from "graphql-request";
import { baseUrl, isStagingEnvironment } from "services/shared/Environment";
import {
  categoriesQuery,
} from "components/optimized/Homepage/Homepage.utils";
import { PagePropsProvider } from "components/optimized/Homepage/Homepage.components";
import { isDevelopment } from "apollo-utilities";
import ContentfulStories from "models/data/contentful/ContentfulStories";
import ContentfulStory from "models/data/contentful/ContentfulStory";
import ContentfulToApiImage from "models/data/mappings/ContentfulToApiImage";
import ContentfulAuthor from "models/data/contentful/ContentfulAuthor";
import { ApiAuthor } from "data/api/ApiAuthor";
import ContentfulImage from "models/data/contentful/ContentfulImage";
import { ApiImage } from "data/api/ApiImage";
import contentful from "libraries/contentful/client";
import ContentfulToApiStory from "models/data/mappings/ContentfulToApiStory";
import { tagsQuery } from "components/optimized/Footer/Footer.constants";

export const IndexPageContainer = styled.div.attrs({
  className: "w-screen flex flex-col items-center justify-center",
})`
  min-height: calc(100vh - 64px);
`;

const IndexPage = ({ categories, categoryStories, latestStories, tags }) => {
  const { userIsLoggedIn, shouldHideHero, showPopup } = useHomepageState();

  return (
    <PagePropsProvider
      categoryStories={categoryStories}
      categories={categories}
      latestStories={latestStories}
      tags={tags}
    >
      <PageContainer>
        <JustSignedUpListener />
        <JustGaveGiftListener />
        <IndexPageContainer>
          {!userIsLoggedIn && !shouldHideHero && (
            <>
              <Hero />
              <About />
            </>
          )}
          <Stories />
        </IndexPageContainer>
        <FooterWithState />
        {showPopup ? <NewsletterPopupForm /> : <></>}
      </PageContainer>
    </PagePropsProvider>
  );
};

function getApiAuthor(cmsAuthor: ContentfulAuthor): ApiAuthor {
  return {
    id: ContentfulAuthor.id(cmsAuthor),
    fullName: ContentfulAuthor.fullName(cmsAuthor),
    slug: ContentfulAuthor.slug(cmsAuthor)
  }
}

function getApiImage(cmsImage: ContentfulImage): ApiImage {
  return ContentfulToApiImage.map(cmsImage)
}

export async function getStaticProps() {
  const apiUrl = isDevelopment()
    ? "http://localhost:3000/api/graphql"
    : isStagingEnvironment()
    ? "https://staging.thejuggernaut.com/api/graphql"
    : "https://www.thejuggernaut.com/api/graphql";

  const result = await request(apiUrl, categoriesQuery());
  const categories = result?.categories ?? [];

  let categoryStories = {}

  const first = 4
  const skip = 0;

  (await Promise.all(
    categories.filter(category => Boolean(category?.id))
    .map(category => ContentfulStories.fetchFromCategory(
      category?.id,
      first, skip
  ))))
  .forEach((cmsStories, index) => {
    const id = categories[index]?.id ?? ''
    categoryStories[id] = !cmsStories ? [] : (cmsStories as ContentfulStory[]).map(cmsStory => {

      const cmsAuthor = ContentfulStory.author(cmsStory);
      const cmsAuthors = ContentfulStory.authorMultiple(cmsStory);
      const cmsFeaturedImage = ContentfulStory.featuredImage(cmsStory);

      return {
        id: ContentfulStory.id(cmsStory),
        title: ContentfulStory.title(cmsStory),
        slug: ContentfulStory.slug(cmsStory),
        publicationDate: ContentfulStory.publicationDate(cmsStory),
        author: getApiAuthor(cmsAuthor),
        authorMultiple: cmsAuthors.map((cmsAuthor) => getApiAuthor(cmsAuthor)),
        summary: ContentfulStory.summary(cmsStory),
        featuredImage: getApiImage(cmsFeaturedImage),
      };
    });
  })

  const fourMostRecentStoryEntries = await contentful.getEntries({
    content_type: "story",
    order: "-fields.publicationDate",
    limit: 4,
    skip: 0,
  });

  const latestStories =
    fourMostRecentStoryEntries?.items?.map((story) => ({
      id: ContentfulStory.id(story),
      title: ContentfulStory.title(story),
      slug: ContentfulStory.slug(story),
      summary: ContentfulStory.summary(story),
      featuredImage: ContentfulToApiStory.apiFeaturedImage(story),
      author: ContentfulToApiStory.apiAuthor(story),
      authorMultiple: ContentfulToApiStory.apiAuthorMultiple(story),
      publicationDate: ContentfulStory.publicationDate(story),
      categories: ContentfulToApiStory.apiCategories(story),
    })) ?? [];
 
  // const result2 = await request(apiUrl, getLatestStoriesQuery(4));

  // const latestStories = result2?.latestStories ?? []

  const tagsResult = await request(apiUrl, tagsQuery);
  const tags = tagsResult?.tags ?? [];

  return {
    props: {
      categories,
      categoryStories,
      latestStories,
      tags
    },
    revalidate: 60,
  };
}

export default IndexPage;
